/**
 * NextHearingCountdown — widget T-7 countdown đến phiên Bán kết / Chung kết
 * tiếp theo. Theo R1→R2 transition doc Bước 5.
 *
 * Logic: Tìm round gần nhất (SEMIFINAL / FINAL) có start_date > now,
 * hiển thị countdown live (refresh 60s).
 */
import { useEffect, useMemo, useState } from 'react'
import {
  Box,
  Paper,
  Typography,
  Stack,
  Chip,
  alpha,
  Skeleton,
  Alert,
} from '@mui/material'
import { Schedule, Gavel } from '@mui/icons-material'
import { useGetRoundsQuery } from '../store/api/roundsApi'
import { useGetCasesQuery } from '../store/api/casesApi'

interface CountdownParts {
  days: number
  hours: number
  minutes: number
  seconds: number
  totalMs: number
  isPast: boolean
}

function calc(targetMs: number, nowMs: number): CountdownParts {
  const diff = targetMs - nowMs
  if (diff <= 0) {
    return { days: 0, hours: 0, minutes: 0, seconds: 0, totalMs: 0, isPast: true }
  }
  const totalSec = Math.floor(diff / 1000)
  const days = Math.floor(totalSec / 86400)
  const hours = Math.floor((totalSec % 86400) / 3600)
  const minutes = Math.floor((totalSec % 3600) / 60)
  const seconds = totalSec % 60
  return { days, hours, minutes, seconds, totalMs: diff, isPast: false }
}

function Cell({ value, label }: { value: number; label: string }) {
  return (
    <Box
      sx={(theme) => ({
        flex: 1,
        textAlign: 'center',
        py: 1.5,
        px: 1,
        borderRadius: 2,
        bgcolor: alpha(theme.palette.primary.main, 0.08),
        border: `1px solid ${alpha(theme.palette.primary.main, 0.18)}`,
      })}
    >
      <Typography variant="h4" fontWeight={700} color="primary.main" sx={{ lineHeight: 1 }}>
        {String(value).padStart(2, '0')}
      </Typography>
      <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 1 }}>
        {label}
      </Typography>
    </Box>
  )
}

export default function NextHearingCountdown() {
  const { data: rounds = [], isLoading: loadingRounds } = useGetRoundsQuery()
  const { data: cases = [], isLoading: loadingCases } = useGetCasesQuery()
  const [nowMs, setNowMs] = useState(() => Date.now())

  // Refresh mỗi 60s — đủ cho countdown days/hours/minutes
  useEffect(() => {
    const id = setInterval(() => setNowMs(Date.now()), 60_000)
    return () => clearInterval(id)
  }, [])

  const upcoming = useMemo(() => {
    const HEARING_TYPES = new Set(['semifinal', 'final'])
    const eligible = rounds
      .filter(
        (r: any) =>
          HEARING_TYPES.has(String(r.round_type)) &&
          r.start_date &&
          new Date(r.start_date).getTime() > nowMs,
      )
      .map((r: any) => ({
        ...r,
        startMs: new Date(r.start_date).getTime(),
        case: cases.find((c: any) => c.id === r.case_id),
      }))
    eligible.sort((a, b) => a.startMs - b.startMs)
    return eligible[0] || null
  }, [rounds, cases, nowMs])

  if (loadingRounds || loadingCases) {
    return (
      <Paper elevation={0} sx={{ p: 3, borderRadius: 3, border: '1px solid', borderColor: 'divider' }}>
        <Skeleton width="60%" height={32} />
        <Skeleton variant="rectangular" height={120} sx={{ mt: 2, borderRadius: 1 }} />
      </Paper>
    )
  }

  if (!upcoming) {
    return (
      <Paper elevation={0} sx={{ p: 3, borderRadius: 3, border: '1px solid', borderColor: 'divider' }}>
        <Stack direction="row" spacing={1} alignItems="center" mb={1}>
          <Gavel color="primary" />
          <Typography variant="h6" fontWeight={600} color="primary.main">
            Phiên xét xử sắp tới
          </Typography>
        </Stack>
        <Alert severity="info" variant="outlined" sx={{ mt: 2 }}>
          Hiện chưa có phiên Bán kết / Chung kết nào được lên lịch.
        </Alert>
      </Paper>
    )
  }

  const parts = calc(upcoming.startMs, nowMs)
  const isUrgent = parts.totalMs > 0 && parts.totalMs < 7 * 24 * 3600 * 1000  // T-7

  return (
    <Paper
      elevation={0}
      sx={(theme) => ({
        p: 3,
        borderRadius: 3,
        border: `1px solid ${theme.palette.divider}`,
        boxShadow: `0 8px 28px ${alpha(theme.palette.primary.main, 0.08)}`,
        background: isUrgent
          ? `linear-gradient(135deg, ${alpha(theme.palette.warning.light, 0.08)} 0%, ${alpha(theme.palette.warning.main, 0.04)} 100%)`
          : undefined,
      })}
    >
      <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between" mb={2}>
        <Stack direction="row" spacing={1} alignItems="center">
          <Gavel color={isUrgent ? 'warning' : 'primary'} />
          <Typography variant="h6" fontWeight={600} color={isUrgent ? 'warning.main' : 'primary.main'}>
            Phiên xét xử sắp tới
          </Typography>
        </Stack>
        {isUrgent && (
          <Chip
            size="small"
            color="warning"
            icon={<Schedule fontSize="small" />}
            label="T-7 trở xuống"
          />
        )}
      </Stack>

      <Box mb={2}>
        <Typography variant="subtitle1" fontWeight={600}>
          {upcoming.case?.title || 'Cuộc thi'}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {upcoming.name} ·{' '}
          {new Date(upcoming.startMs).toLocaleString('vi-VN', {
            weekday: 'long',
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
          })}
        </Typography>
      </Box>

      <Stack direction="row" spacing={1.5}>
        <Cell value={parts.days} label="ngày" />
        <Cell value={parts.hours} label="giờ" />
        <Cell value={parts.minutes} label="phút" />
      </Stack>
    </Paper>
  )
}
